import DOMPurify from 'dompurify';
import delay from 'lodash/delay';
import { useState } from 'react';

import {
  FILTER_SET_NAME_MAX_LENGTH,
  type FilterSetScope,
  toFilterSetNameKey,
} from '@revelio/iso-utility';
import { Button, ButtonGroup, Input, Text } from '@revelio/ui';

import { useToast } from '../../hooks/useToast';
import styles from './CreateSavedViewForm.module.css';
import { type SavedView, useSavedViews } from './useSavedViews';

export interface CreateSavedViewFormProps {
  scope: FilterSetScope;
  onClose: () => void;
  onCreated?: (item: SavedView) => void;
  onOverwritten?: (item: SavedView) => void;
  isDisabled?: boolean;
  /** What the set records. See SavedViewsButton. */
  currentUrl: () => string;
  overwriteSlot?: (item: SavedView) => React.ReactNode;
}

export const CreateSavedViewForm = ({
  scope,
  onClose,
  onCreated,
  onOverwritten,
  isDisabled,
  currentUrl,
  overwriteSlot,
}: CreateSavedViewFormProps) => {
  const {
    options: allSavedViews,
    createView,
    updateView,
    refresh,
  } = useSavedViews();

  const toast = useToast();

  const [nameInput, setNameInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const [itemToOverwrite, setItemToOverwrite] = useState<SavedView | undefined>(
    undefined
  );

  const showToast = (title: string, status: 'success' | 'error') => {
    toast({
      title,
      status,
      position: 'top-right',
      duration: 4000,
      isClosable: true,
    });
  };

  const sanitizedName = () =>
    DOMPurify.sanitize(nameInput, { ALLOWED_TAGS: [] }).trim();

  const findClashingSet = (name: string, conflictingId?: string) =>
    allSavedViews.find(
      (set) =>
        (conflictingId && set.id === conflictingId) ||
        toFilterSetNameKey(set.name) === toFilterSetNameKey(name)
    );

  const handleOverwriteSavedView = () => {
    if (!itemToOverwrite) return;
    const name = sanitizedName();
    if (name.length === 0) {
      showToast('View name is invalid', 'error');
      return;
    }
    const url = currentUrl();
    setIsLoading(true);
    updateView({
      id: itemToOverwrite.id,
      name,
      scope,
      url,
    })
      .then((res) => {
        if (res.error) {
          showToast('Error overwriting view', 'error');
        } else {
          showToast('View overwritten', 'success');
          onOverwritten?.({ ...itemToOverwrite, name, scope, url });
        }
      })
      .finally(() => {
        delay(() => {
          setIsLoading(false);
          setItemToOverwrite(undefined);
          onClose();
        }, 100);
      });
  };

  const promptOverwrite = (
    name: string,
    error: { message: string; conflictingId?: string }
  ) => {
    const existing = findClashingSet(name, error.conflictingId);
    if (!existing) {
      refresh();
      showToast(error.message || 'Error creating view', 'error');
      return;
    }
    setItemToOverwrite(existing);
  };

  const handleCreate = () => {
    const name = sanitizedName();
    if (name.length === 0) {
      showToast('View name is invalid', 'error');
      return;
    }
    const localClash = findClashingSet(name);
    if (localClash) {
      setItemToOverwrite(localClash);
      return;
    }

    const url = currentUrl();
    setIsLoading(true);
    createView({ name, scope, url }).then((res) => {
      if (res.error?.isConflict) {
        setIsLoading(false);
        promptOverwrite(name, res.error);
        return;
      }
      if (res.error) {
        setIsLoading(false);
        showToast('Error creating view', 'error');
        return;
      }
      showToast('View created', 'success');
      onCreated?.({ id: res.id ?? '', name, scope, url });
      delay(() => {
        setIsLoading(false);
        onClose();
      }, 100);
    });
  };

  return itemToOverwrite ? (
    <>
      {overwriteSlot ? (
        overwriteSlot(itemToOverwrite)
      ) : (
        <Text fontSize="sm">
          Are you sure you want to overwrite this saved view?
        </Text>
      )}
      <ButtonGroup size="sm" className={styles.overwriteActions}>
        <Button
          variant="ghost"
          color="secondary"
          onClick={() => setItemToOverwrite(undefined)}
        >
          Cancel
        </Button>
        <Button
          color="destructive"
          loading={isLoading}
          onClick={handleOverwriteSavedView}
        >
          Overwrite
        </Button>
      </ButtonGroup>
    </>
  ) : (
    <>
      <Input
        id="saved-view-name"
        data-testid="saved-view-name-input"
        className={styles.nameInput}
        size="sm"
        type="text"
        placeholder="Enter a name"
        maxLength={FILTER_SET_NAME_MAX_LENGTH}
        value={nameInput}
        onChange={(e) => setNameInput(e.target.value)}
      />
      <ButtonGroup size="sm" className={styles.saveActions}>
        <Button size="sm" variant="ghost" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={nameInput.length === 0 || isLoading || isDisabled}
          onClick={handleCreate}
          loading={isLoading}
          data-testid="saved-view-save-button"
        >
          Save
        </Button>
      </ButtonGroup>
    </>
  );
};
